import { useCallback } from 'react';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import { CadenceTranslationKeys, MONTHLY, ScheduleWindowTranslationKeys } from 'automation-ui-enrollment/constants/scheduledEnrollment/ScheduleCadence';
import { DayOfWeekTranslationKeys } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfWeekValues';
import { DayOfMonthFlexValues } from 'automation-ui-enrollment/constants/scheduledEnrollment/DayOfMonthValues';
import ErrorBoundary from 'automation-ui-core/errors/ErrorBoundary';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { jsx as _jsx } from "react/jsx-runtime";
const NO_DAY_OF_WEEK_VALUES = [];
const NO_DAY_OF_MONTH_VALUES = [];
const NO_MONTH_VALUES = [];
const NO_YEAR_VALUES = [];
function ScheduledEnrollmentDetails({
  enrollmentSchedule,
  objectTypeId,
  showTimeZone = true
}) {
  var _enrollmentSchedule$d;
  const cadence = enrollmentSchedule.getCadence();
  const scheduleWindow = enrollmentSchedule.getScheduleWindow();
  const scheduleHour = enrollmentSchedule.getHour();
  const scheduleMinute = enrollmentSchedule.getMinute();
  const dayOfWeekValues = enrollmentSchedule.getDayOfWeekValues() || NO_DAY_OF_WEEK_VALUES;
  const dayOfMonthValues = enrollmentSchedule.getDayOfMonthValues() || NO_DAY_OF_MONTH_VALUES;
  const monthValues = enrollmentSchedule.getMonthValues() || NO_MONTH_VALUES;
  const yearValues = enrollmentSchedule.getYearValues() || NO_YEAR_VALUES;
  const dateProperty = enrollmentSchedule.dateProperty;
  const flexDayOfMonthValue = enrollmentSchedule.getDayOfMonthFlexValue();
  const isSpecificTime = scheduleWindow === 'SPECIFIC_TIME';
  const TimeMessage = useCallback(() => {
    return /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedDateTime, {
        "data-test-id": "scheduled-enrollment-action-detail-time",
        value: I18n.moment.portalTz(`${scheduleHour}:${scheduleMinute}`, 'H:mm').valueOf(),
        format: showTimeZone ? 'LT z' : 'LT'
      })
    });
  }, [scheduleHour, scheduleMinute, showTimeZone]);
  const ScheduleWindowTimeMessage = useCallback(() => {
    var _ScheduleWindowTransl, _ScheduleWindowTransl2;
    return /*#__PURE__*/_jsx("strong", {
      children: (_ScheduleWindowTransl = (_ScheduleWindowTransl2 = ScheduleWindowTranslationKeys[scheduleWindow]).getDisplayText) === null || _ScheduleWindowTransl === void 0 ? void 0 : _ScheduleWindowTransl.call(_ScheduleWindowTransl2, {
        showTimeZone
      })
    });
  }, [scheduleWindow, showTimeZone]);
  const ListOfDays = useCallback(() => /*#__PURE__*/_jsx(FormattedList, {
    children: dayOfWeekValues.map(dayValue => /*#__PURE__*/_jsx("strong", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: DayOfWeekTranslationKeys[dayValue]
      })
    }, dayValue))
  }), [dayOfWeekValues]);
  const FlexDayOfMonthDescription = useCallback(() => /*#__PURE__*/_jsx("strong", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: DayOfMonthFlexValues[flexDayOfMonthValue].descriptionKey
    })
  }), [flexDayOfMonthValue]);
  const ListOfDaysOfMonth = useCallback(() => /*#__PURE__*/_jsx(FormattedList, {
    children: dayOfMonthValues.map(dayOfMonthValue => /*#__PURE__*/_jsx("strong", {
      children: dayOfMonthValue
    }, dayOfMonthValue))
  }), [dayOfMonthValues]);
  const DateMessage = useCallback(() => dateProperty ? /*#__PURE__*/_jsx(PropertyName, {
    propertyName: dateProperty.propertyName,
    objectTypeId: objectTypeId
  }) : /*#__PURE__*/_jsx("strong", {
    children: /*#__PURE__*/_jsx(FormattedDateTime, {
      value: I18n.moment.portalTz(`${monthValues[0]}/${dayOfMonthValues[0]}`, 'MM/DD').valueOf(),
      format: "medium-date"
    })
  }), [dateProperty, objectTypeId, monthValues, dayOfMonthValues]);
  const DateWithYearMessage = useCallback(() => dateProperty ? /*#__PURE__*/_jsx(PropertyName, {
    propertyName: dateProperty.propertyName,
    objectTypeId: objectTypeId
  }) : /*#__PURE__*/_jsx("strong", {
    children: /*#__PURE__*/_jsx(FormattedDateTime, {
      value: I18n.moment.portalTz(`${yearValues[0]}/${monthValues[0]}/${dayOfMonthValues[0]}`, 'YYYY/MM/DD').valueOf(),
      format: "ll"
    })
  }), [dateProperty, objectTypeId, yearValues, monthValues, dayOfMonthValues]);
  let descriptionKey = CadenceTranslationKeys[cadence].descriptionKey;
  /* Time range specific translations: i.e "Every day between 10am and 1pm"*/
  if (!isSpecificTime && CadenceTranslationKeys[cadence].timeRangeDescriptionKey) {
    descriptionKey = CadenceTranslationKeys[cadence].timeRangeDescriptionKey;
  }

  /* Switch for translations that include a delta, and considerations for timerange versus specific time */
  const delta = (_enrollmentSchedule$d = enrollmentSchedule.dateProperty) === null || _enrollmentSchedule$d === void 0 ? void 0 : _enrollmentSchedule$d.delta.delta;
  if (delta !== undefined && delta < 0 && isSpecificTime && CadenceTranslationKeys[cadence].beforeSpecificTimeDescriptionKey) {
    descriptionKey = CadenceTranslationKeys[cadence].beforeSpecificTimeDescriptionKey;
  } else if (delta !== undefined && delta > 0 && isSpecificTime && CadenceTranslationKeys[cadence].afterSpecificTimeDescriptionKey) {
    descriptionKey = CadenceTranslationKeys[cadence].afterSpecificTimeDescriptionKey;
  } else if (delta !== undefined && delta < 0 && !isSpecificTime && CadenceTranslationKeys[cadence].beforeTimeRangeDescriptionKey) {
    descriptionKey = CadenceTranslationKeys[cadence].beforeTimeRangeDescriptionKey;
  } else if (delta !== undefined && delta > 0 && !isSpecificTime && CadenceTranslationKeys[cadence].afterTimeRangeDescriptionKey) {
    descriptionKey = CadenceTranslationKeys[cadence].afterTimeRangeDescriptionKey;
  }

  /* Special case for flex day of month values */
  if (cadence === MONTHLY && flexDayOfMonthValue) {
    descriptionKey = isSpecificTime ? 'automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleLabel.monthlyFlexDescription_jsx' : 'automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleLabel.monthlyFlexDescriptionTimeRange_jsx';
  }

  // Wrap the resulting formatted message in an error boundary to account for potentially invalid dates.
  // The only known case this occurs is when a user selects an annual trigger of Feb 29, which
  // is a valid date but not of every year.
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "VirtualScheduledEnrollmentActionDetail_InvalidDateError",
    renderError: error => {
      console.warn(error);
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: isSpecificTime ? 'automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleLabel.yearlyDescription.specificTime_fallback_jsx' : 'automation-ui-enrollment.redesign.scheduledTriggers.tabs.enrollment.scheduleLabel.yearlyDescription.timeRange_fallback_jsx',
        options: {
          dayNumber: /*#__PURE__*/_jsx("strong", {
            children: dayOfMonthValues[0]
          }),
          monthName: /*#__PURE__*/_jsx("strong", {
            children: I18n.moment.localeData().months()[monthValues[0] - 1]
          })
        },
        elements: {
          TimeMessage,
          ScheduleWindowTimeMessage
        }
      });
    },
    children: descriptionKey && /*#__PURE__*/_jsx(FormattedJSXMessage, {
      message: descriptionKey,
      elements: {
        DateWithYearMessage,
        DateMessage,
        ListOfDays,
        ListOfDaysOfMonth,
        TimeMessage,
        ScheduleWindowTimeMessage,
        FlexDayOfMonthDescription,
        Bold: 'strong'
      },
      options: delta ? {
        count: Math.abs(delta)
      } : {}
    })
  });
}
export default ScheduledEnrollmentDetails;